/**
 * HTML sanitization utilities for label fields
 *
 * These utilities handle HTML tag sanitization for labels:
 * - Current: Display rendering of stored HTML values
 * - Future: Input sanitization when HTML input is allowed
 *
 * All utilities are designed to be reusable for both scenarios.
 */
import DOMPurify from 'dompurify';

/**
 * List of allowed HTML tags for labels
 * These tags can be nested within each other
 *
 * Examples:
 * - <p><b>bold</b></p> ✅
 * - <h1><span>text</span></h1> ✅
 * - <ul><li><b>item</b></li></ul> ✅
 * - <ol><li>step</li></ol> ✅
 * - <p><font color="red"><b>bold red</b></font></p> ✅
 * - <table><thead><tr><th/></tr></thead><tbody><tr><td/></tr></tbody></table> ✅
 */
const ALLOWED_TAGS = [
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'p',
  'b',
  'a',
  'br',
  'em',
  'span',
  'font',
  'i',
  'u',
  'div',
  'strong',
  'colgroup',
  'caption',
  'tbody',
  'thead',
  'tfoot',
  'table',
  'tr',
  'th',
  'td',
  'col',
  'li',
  'ul',
  'ol',
] as const;

/** Longest tag names first so the detector regex matches e.g. colgroup before col */
const ALLOWED_TAGS_FOR_REGEX = [...ALLOWED_TAGS].sort(
  (a, b) => b.length - a.length
);

/**
 * Strips ALL HTML tags from input
 *
 * Current Use: Input field (prevents HTML entry)
 * Future: Can be replaced with sanitizeLabelHtml() when HTML input is allowed
 *
 * @param input - The string to strip HTML tags from
 * @returns String with all HTML tags removed
 */
export const stripHtmlTags = (input: string): string => {
  if (!input) {
    return '';
  }

  return input.replace(/<[^>]*>/g, '');
};

/**
 * Sanitizes HTML to only allow specified tags
 *
 * Current Use: Display rendering (when HTML exists in stored values)
 * Future Use: Input sanitization (when HTML input is allowed)
 *
 * This function is designed to be reusable for both scenarios.
 * It preserves nested tags and content even when disallowed tags are removed.
 *
 * Behavior with disallowed tags:
 * - Disallowed tags are REMOVED (security)
 * - Content inside disallowed tags is PRESERVED (user-friendly)
 * - Allowed tags remain intact
 *
 * Examples:
 * - Input:  "<p><b>Bold</b> and <script>alert(1)</script></p>"
 *   Output: "<p><b>Bold</b> and alert(1)</p>" (script tag removed, content kept)
 *
 * - Input:  "<p><b>Bold</b> and <strong>Strong</strong></p>"
 *   Output: "<p><b>Bold</b> and Strong</p>" (strong tag removed, content kept)
 *
 * - Input:  "<div><p>Paragraph</p></div>"
 *   Output: "<p>Paragraph</p>" (div tag removed, nested p preserved)
 *
 * @param input - The HTML string to sanitize
 * @returns Sanitized HTML string with only allowed tags
 */
export const sanitizeLabelHtml = (input: string): string => {
  if (!input) {
    return '';
  }

  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [
      'color',
      'size',
      'face',
      'href',
      'style',
      'colspan',
      'rowspan',
      'scope',
      'headers',
      'abbr',
      'align',
      'valign',
      'border',
      'cellpadding',
      'cellspacing',
      'width',
      'height',
      'bgcolor',
    ],
    KEEP_CONTENT: true,
  });
};

/**
 * Checks if input contains allowed HTML tags
 *
 * Current Use: Detection for rendering decision
 * Future Use: Input validation (when HTML input is allowed)
 *
 * Works with nested tags - detects tags at any nesting level.
 *
 * @param input - The string to check for HTML tags
 * @returns True if input contains any allowed HTML tags
 */
export const containsAllowedHtmlTags = (input: string): boolean => {
  if (!input) {
    return false;
  }

  // Regex matches tags at any nesting level
  // Matches: <tag>, <tag >, <tag/>, <tag />, <tag attr="...">
  const tagPattern = new RegExp(
    `<(${ALLOWED_TAGS_FOR_REGEX.join('|')})(?:[\\s/>])`,
    'i'
  );

  return tagPattern.test(input);
};

/**
 * Checks if input contains ANY HTML tags (allowed or not)
 *
 * Current Use: Validation (prevent HTML entry)
 * Future Use: Validation (warn about disallowed tags)
 *
 * @param input - The string to check for HTML tags
 * @returns True if input contains any HTML tags
 */
export const containsAnyHtmlTags = (input: string): boolean => {
  if (!input) {
    return false;
  }

  const htmlRegex = /<[^>]*>/g;

  return htmlRegex.test(input);
};
